import { surfacesFps } from '../data/full-surfaces';

export interface SurfaceFpsEntry {
    id: string;
    options: Record<string, string>;
    version: number;
}

export function tryExtractFpsFromUrl(url: string): SurfaceFpsEntry | undefined {
    try {
        const urlObj = new URL(url);
        // Matches /products/:productId/:version
        const productPathRegex = /\/products\/([^/]+)\/(\d+)/i;
        const match = urlObj.pathname.match(productPathRegex);

        if (match) {
            const productId = match[1];
            const version = parseInt(match[2], 10);
            const options: Record<string, string> = {};

            // 1. Try case-insensitive optionSelections parameter containing JSON object
            let optionSelectionsParam = urlObj.searchParams.get('optionSelections');
            if (!optionSelectionsParam) {
                for (const [key, value] of urlObj.searchParams.entries()) {
                    if (key.toLowerCase() === 'optionselections') {
                        optionSelectionsParam = value;
                        break;
                    }
                }
            }

            if (optionSelectionsParam) {
                try {
                    const parsed = JSON.parse(optionSelectionsParam);
                    if (parsed && typeof parsed === 'object') {
                        Object.assign(options, parsed);
                    }
                } catch {
                    // Ignore JSON parsing errors
                }
            }

            // 2. Parse individual query parameters in format: optionSelections[Key]=Value
            for (const [key, value] of urlObj.searchParams.entries()) {
                const optMatch = key.match(/^optionSelections\[(.*)\]$/i);
                if (optMatch) {
                    options[optMatch[1]] = value;
                }
            }

            return {
                id: productId,
                options,
                version,
            };
        }
    } catch {
        // Silently fail if not a valid URL or parsing fails
    }
    return undefined;
}

/**
 * Searches the local surfacesFps database for a given surface URL.
 * Handles both direct URL matches and translated standard calculatedSurfaceSets URLs.
 */
export function lookupSurfaceFps(url: string): SurfaceFpsEntry | undefined {
    try {
        const decodedInput = decodeURIComponent(url).toLowerCase();

        // 2. Direct match on key
        const directMatchKey = Object.keys(surfacesFps).find((key) => {
            try {
                return decodeURIComponent(key).toLowerCase() === decodedInput;
            } catch {
                return key.toLowerCase() === decodedInput;
            }
        });

        if (directMatchKey) {
            return surfacesFps[directMatchKey];
        }

        // 3. Match translated standard SKU URL
        // Example: https://surface.products.cimpress.io/v3/calculatedsurfacesets/CIM-HBGEVEJ7?variables=...
        const urlObj = new URL(url);
        const mcpSku = urlObj.pathname.split('/').find((x) => new RegExp(/[A-Z]{3}-[A-Z0-9]{8}/, 'g').test(x));

        if (mcpSku) {
            const variables = urlObj.searchParams.get('variables');
            const variablesObj = variables ? JSON.parse(variables) : undefined;

            if (variablesObj) {
                const matchKey = Object.keys(surfacesFps).find((key) => {
                    const entry = surfacesFps[key];
                    if (entry.id.toLowerCase() !== mcpSku.toLowerCase()) {
                        return false;
                    }

                    // Compare options keys and values (order-insensitive)
                    const entryKeys = Object.keys(entry.options);
                    const varKeys = Object.keys(variablesObj);

                    if (entryKeys.length !== varKeys.length) {
                        return false;
                    }

                    return entryKeys.every((k) => entry.options[k] === variablesObj[k]);
                });

                if (matchKey) {
                    return surfacesFps[matchKey];
                }
            }
        }
    } catch (e) {
        console.error('Error lookupSurfaceFps:', e);
    }

    return undefined;
}

export function parseOptionsFromSurfaceUrl(url: string): Record<string, string> {
    try {
        const urlObj = new URL(url);

        // Try standard option parameters first
        const optionsParam = urlObj.searchParams.get('optionSelections') || urlObj.searchParams.get('variables');
        if (optionsParam) {
            const optionPairs = optionsParam.split(',');
            const options: Record<string, string> = {};

            for (const pair of optionPairs) {
                const [key, value] = pair.split(':');
                if (key && value) {
                    options[key.trim()] = value.trim();
                }
            }
            return options;
        }

        // For calculatedSurfaceSets, collect all query params as options
        const options: Record<string, string> = {};
        urlObj.searchParams.forEach((value, key) => {
            // Exclude meta parameters that aren't product attributes
            if (!['surfaceSetLink', 'Prepress URL', 'Prepress Parameters URL', '_variantId'].includes(key)) {
                options[key] = value;
            }
        });

        return options;
    } catch (e) {
        console.error('Error parsing options from surface URL:', e);
        return {};
    }
}
